/** Ask's strings (the Ask page and its button), merged into the app's translation namespace on import. */
import i18n from '@/app/i18n'

const en = {
    'ask.title': 'Analyst',
    'ask.beta': 'BETA',
    'ask.button': 'Analyst',
    'ask.newQuestion': 'New question',
    'ask.threads': 'Questions',
    'ask.today': 'Today',
    'ask.earlier': 'Earlier',
    'ask.noThreads': 'Your questions show here.',
    'ask.archive': 'Archive',
    'ask.restore': 'Restore',
    'ask.archived': 'Archived',
    'ask.archivedCount': 'Archived ({{n}})',
    'ask.backToThreads': 'Back to questions',
    'ask.noArchived': 'Nothing archived.',
    'ask.archivedNote': 'Archived. A new question brings it back.',
    'ask.archiveFailed': 'That didn’t work. Try again.',
    'ask.emptyTitle': 'Ask your data',
    'ask.emptyHint': 'Answers come from this workspace’s data only, and every number from a query.',
    'ask.inputLabel': 'Question',
    'ask.placeholder': 'Ask your data…',
    'ask.placeholderNext': 'Ask a follow-up…',
    'ask.send': 'Ask',
    'ask.context.all': 'All data',
    'ask.context.page': 'Going by this page: {{page}}',
    'ask.context.drop': 'Don’t go by this page',
    'ask.askedOn': 'Asked on {{page}}',
    'ask.section.skill': 'Skill',
    'ask.section.data': 'Your data',
    'ask.section.check': 'Check',
    'ask.stage.start': 'Reads the question',
    'ask.stage.skill': 'Picks a skill',
    'ask.stage.data': 'Reads your data',
    'ask.stage.check': 'Checks the numbers',
    'ask.stage.done': 'Done',
    'ask.stage.stopped': 'Stopped',
    'ask.count.queries_one': '{{count}} query',
    'ask.count.queries_other': '{{count}} queries',
    'ask.count.rows_one': '{{count}} row',
    'ask.count.rows_other': '{{count}} rows',
    'ask.count.views_one': '{{count}} view',
    'ask.count.views_other': '{{count}} views',
    'ask.reading': 'reading…',
    'ask.stepFailed': 'failed',
    'ask.skillRead': 'read',
    'ask.check.label': 'Numbers from queries',
    'ask.check.running': 'checking…',
    'ask.check.ok': 'Numbers checked',
    'ask.check.unverified_one': '{{count}} number unchecked',
    'ask.check.unverified_other': '{{count}} numbers unchecked',
    'ask.kind.answer': 'Answer',
    'ask.kind.ask_back': 'Which one?',
    'ask.kind.out_of_data': 'Not in the data',
    'ask.basedOn_one': 'Based on {{count}} query',
    'ask.basedOn_other': 'Based on {{count}} queries',
    'ask.showQueries': 'Show queries',
    'ask.hideQueries': 'Hide queries',
    'ask.next': 'Ask next',
    'ask.group.action': 'What to do',
    'ask.group.reason': 'Why',
    'ask.choose': 'Choose',
    'ask.moreRows_one': '{{count}} more row',
    'ask.moreRows_other': '{{count}} more rows',
    'ask.examples': 'For example',
    'ask.openFull': 'Open in full view',
    'ask.close': 'Close',
    'ask.error.failed': 'No answer came. Try again.',
    'ask.error.no_answer': 'No answer came. Try again.',
    'ask.error.over_budget': 'This month’s Analyst budget is used up.',
    'ask.error.not_configured': 'The Analyst isn’t available right now.',
    'ask.error.forbidden_by_role': 'Your role doesn’t have the Analyst.',
    'ask.error.stopped': 'The question stopped before an answer.',
    'ask.loadFailed': 'The questions could not be loaded.',
    'ask.retry': 'Try again',
    'ask.budget': '{{share}} of this month’s Analyst budget is used.',
}

const et: Record<keyof typeof en, string> & Record<string, string> = {
    'ask.title': 'Analüütik',
    'ask.beta': 'BETA',
    'ask.button': 'Analüütik',
    'ask.newQuestion': 'Uus küsimus',
    'ask.threads': 'Küsimused',
    'ask.today': 'Täna',
    'ask.earlier': 'Varem',
    'ask.noThreads': 'Siin on sinu küsimused.',
    'ask.archive': 'Arhiveeri',
    'ask.restore': 'Taasta',
    'ask.archived': 'Arhiiv',
    'ask.archivedCount': 'Arhiiv ({{n}})',
    'ask.backToThreads': 'Tagasi küsimuste juurde',
    'ask.noArchived': 'Arhiivis pole midagi.',
    'ask.archivedNote': 'Arhiveeritud. Uus küsimus toob selle tagasi.',
    'ask.archiveFailed': 'See ei õnnestunud. Proovi uuesti.',
    'ask.emptyTitle': 'Küsi oma andmetelt',
    'ask.emptyHint': 'Vastused tulevad ainult selle tööruumi andmetest ja iga number päringust.',
    'ask.inputLabel': 'Küsimus',
    'ask.placeholder': 'Küsi oma andmetelt…',
    'ask.placeholderNext': 'Küsi edasi…',
    'ask.send': 'Küsi',
    'ask.context.all': 'Kõik andmed',
    'ask.context.page': 'Selle lehe järgi: {{page}}',
    'ask.context.drop': 'Ära lähtu sellest lehest',
    'ask.askedOn': 'Küsitud lehelt {{page}}',
    'ask.section.skill': 'Oskus',
    'ask.section.data': 'Sinu andmed',
    'ask.section.check': 'Kontroll',
    'ask.stage.start': 'Loeb küsimust',
    'ask.stage.skill': 'Valib oskuse',
    'ask.stage.data': 'Loeb sinu andmeid',
    'ask.stage.check': 'Kontrollib numbreid',
    'ask.stage.done': 'Valmis',
    'ask.stage.stopped': 'Peatus',
    'ask.count.queries_one': '{{count}} päring',
    'ask.count.queries_other': '{{count}} päringut',
    'ask.count.rows_one': '{{count}} rida',
    'ask.count.rows_other': '{{count}} rida',
    'ask.count.views_one': '{{count}} vaade',
    'ask.count.views_other': '{{count}} vaadet',
    'ask.reading': 'loeb…',
    'ask.stepFailed': 'ei õnnestunud',
    'ask.skillRead': 'loetud',
    'ask.check.label': 'Numbrid päringutest',
    'ask.check.running': 'kontrollib…',
    'ask.check.ok': 'Numbrid kontrollitud',
    'ask.check.unverified_one': '{{count}} number kontrollimata',
    'ask.check.unverified_other': '{{count}} numbrit kontrollimata',
    'ask.kind.answer': 'Vastus',
    'ask.kind.ask_back': 'Täpsusta',
    'ask.kind.out_of_data': 'Andmetes pole',
    'ask.basedOn_one': 'Põhineb {{count}} päringul',
    'ask.basedOn_other': 'Põhineb {{count}} päringul',
    'ask.showQueries': 'Näita päringuid',
    'ask.hideQueries': 'Peida päringud',
    'ask.next': 'Küsi edasi',
    'ask.group.action': 'Mida teha',
    'ask.group.reason': 'Miks',
    'ask.choose': 'Vali',
    'ask.moreRows_one': 'ja veel {{count}} rida',
    'ask.moreRows_other': 'ja veel {{count}} rida',
    'ask.examples': 'Näiteks',
    'ask.openFull': 'Ava täisvaates',
    'ask.close': 'Sulge',
    'ask.error.failed': 'Vastust ei tulnud. Proovi uuesti.',
    'ask.error.no_answer': 'Vastust ei tulnud. Proovi uuesti.',
    'ask.error.over_budget': 'Selle kuu Analüütiku eelarve on kasutatud.',
    'ask.error.not_configured': 'Analüütik pole praegu saadaval.',
    'ask.error.forbidden_by_role': 'Sinu rollil pole Analüütikut.',
    'ask.error.stopped': 'Küsimus peatus enne vastust.',
    'ask.loadFailed': 'Küsimusi ei saanud laadida.',
    'ask.retry': 'Proovi uuesti',
    'ask.budget': 'Selle kuu Analüütiku eelarvest on kasutatud {{share}}.',
}

const de: Record<string, string> = {
    'ask.title': 'Analyst',
    'ask.beta': 'BETA',
    'ask.button': 'Analyst',
    'ask.newQuestion': 'Neue Frage',
    'ask.threads': 'Fragen',
    'ask.today': 'Heute',
    'ask.earlier': 'Früher',
    'ask.noThreads': 'Hier erscheinen Ihre Fragen.',
    'ask.archive': 'Archivieren',
    'ask.restore': 'Wiederherstellen',
    'ask.archived': 'Archiv',
    'ask.archivedCount': 'Archiv ({{n}})',
    'ask.backToThreads': 'Zurück zu den Fragen',
    'ask.noArchived': 'Nichts archiviert.',
    'ask.archivedNote': 'Archiviert. Eine neue Frage holt es zurück.',
    'ask.archiveFailed': 'Das hat nicht geklappt. Bitte erneut versuchen.',
    'ask.emptyTitle': 'Fragen Sie Ihre Daten',
    'ask.emptyHint': 'Antworten kommen nur aus den Daten dieses Arbeitsbereichs, jede Zahl aus einer Abfrage.',
    'ask.inputLabel': 'Frage',
    'ask.placeholder': 'Fragen Sie Ihre Daten…',
    'ask.placeholderNext': 'Weiter fragen…',
    'ask.send': 'Fragen',
    'ask.context.all': 'Alle Daten',
    'ask.context.page': 'Nach dieser Seite: {{page}}',
    'ask.context.drop': 'Nicht nach dieser Seite',
    'ask.askedOn': 'Gefragt auf {{page}}',
    'ask.section.skill': 'Fähigkeit',
    'ask.section.data': 'Ihre Daten',
    'ask.section.check': 'Prüfung',
    'ask.stage.start': 'Liest die Frage',
    'ask.stage.skill': 'Wählt eine Fähigkeit',
    'ask.stage.data': 'Liest Ihre Daten',
    'ask.stage.check': 'Prüft die Zahlen',
    'ask.stage.done': 'Fertig',
    'ask.stage.stopped': 'Angehalten',
    'ask.count.queries_one': '{{count}} Abfrage',
    'ask.count.queries_other': '{{count}} Abfragen',
    'ask.count.rows_one': '{{count}} Zeile',
    'ask.count.rows_other': '{{count}} Zeilen',
    'ask.count.views_one': '{{count}} Sicht',
    'ask.count.views_other': '{{count}} Sichten',
    'ask.reading': 'liest…',
    'ask.stepFailed': 'fehlgeschlagen',
    'ask.skillRead': 'gelesen',
    'ask.check.label': 'Zahlen aus Abfragen',
    'ask.check.running': 'prüft…',
    'ask.check.ok': 'Zahlen geprüft',
    'ask.check.unverified_one': '{{count}} Zahl ungeprüft',
    'ask.check.unverified_other': '{{count}} Zahlen ungeprüft',
    'ask.kind.answer': 'Antwort',
    'ask.kind.ask_back': 'Welche?',
    'ask.kind.out_of_data': 'Nicht in den Daten',
    'ask.basedOn_one': 'Aus {{count}} Abfrage',
    'ask.basedOn_other': 'Aus {{count}} Abfragen',
    'ask.showQueries': 'Abfragen zeigen',
    'ask.hideQueries': 'Abfragen ausblenden',
    'ask.next': 'Weiter fragen',
    'ask.group.action': 'Was tun',
    'ask.group.reason': 'Warum',
    'ask.choose': 'Wählen',
    'ask.moreRows_one': 'und {{count}} weitere Zeile',
    'ask.moreRows_other': 'und {{count}} weitere Zeilen',
    'ask.examples': 'Zum Beispiel',
    'ask.openFull': 'In voller Ansicht öffnen',
    'ask.close': 'Schließen',
    'ask.error.failed': 'Keine Antwort erhalten. Bitte erneut versuchen.',
    'ask.error.no_answer': 'Keine Antwort erhalten. Bitte erneut versuchen.',
    'ask.error.over_budget': 'Das Analyst-Budget dieses Monats ist aufgebraucht.',
    'ask.error.not_configured': 'Der Analyst ist gerade nicht verfügbar.',
    'ask.error.forbidden_by_role': 'Ihre Rolle hat keinen Analysten.',
    'ask.error.stopped': 'Die Frage hielt vor einer Antwort an.',
    'ask.loadFailed': 'Die Fragen konnten nicht geladen werden.',
    'ask.retry': 'Erneut versuchen',
    'ask.budget': '{{share}} des Analyst-Budgets dieses Monats sind verbraucht.',
}

const ru: Record<string, string> = {
    'ask.title': 'Аналитик',
    'ask.beta': 'BETA',
    'ask.button': 'Аналитик',
    'ask.newQuestion': 'Новый вопрос',
    'ask.threads': 'Вопросы',
    'ask.today': 'Сегодня',
    'ask.earlier': 'Ранее',
    'ask.noThreads': 'Здесь появятся ваши вопросы.',
    'ask.archive': 'Архивировать',
    'ask.restore': 'Восстановить',
    'ask.archived': 'Архив',
    'ask.archivedCount': 'Архив ({{n}})',
    'ask.backToThreads': 'Назад к вопросам',
    'ask.noArchived': 'В архиве пусто.',
    'ask.archivedNote': 'В архиве. Новый вопрос вернёт его.',
    'ask.archiveFailed': 'Не получилось. Попробуйте ещё раз.',
    'ask.emptyTitle': 'Спросите свои данные',
    'ask.emptyHint': 'Ответы берутся только из данных этого рабочего пространства, каждое число — из запроса.',
    'ask.inputLabel': 'Вопрос',
    'ask.placeholder': 'Спросите свои данные…',
    'ask.placeholderNext': 'Спросите дальше…',
    'ask.send': 'Спросить',
    'ask.context.all': 'Все данные',
    'ask.context.page': 'По этой странице: {{page}}',
    'ask.context.drop': 'Не по этой странице',
    'ask.askedOn': 'Спрошено на странице {{page}}',
    'ask.section.skill': 'Навык',
    'ask.section.data': 'Ваши данные',
    'ask.section.check': 'Проверка',
    'ask.stage.start': 'Читает вопрос',
    'ask.stage.skill': 'Выбирает навык',
    'ask.stage.data': 'Читает ваши данные',
    'ask.stage.check': 'Проверяет числа',
    'ask.stage.done': 'Готово',
    'ask.stage.stopped': 'Остановлено',
    'ask.count.queries_one': '{{count}} запрос',
    'ask.count.queries_few': '{{count}} запроса',
    'ask.count.queries_many': '{{count}} запросов',
    'ask.count.queries_other': '{{count}} запроса',
    'ask.count.rows_one': '{{count}} строка',
    'ask.count.rows_few': '{{count}} строки',
    'ask.count.rows_many': '{{count}} строк',
    'ask.count.rows_other': '{{count}} строки',
    'ask.count.views_one': '{{count}} представление',
    'ask.count.views_few': '{{count}} представления',
    'ask.count.views_many': '{{count}} представлений',
    'ask.count.views_other': '{{count}} представления',
    'ask.reading': 'читает…',
    'ask.stepFailed': 'не удалось',
    'ask.skillRead': 'прочитан',
    'ask.check.label': 'Числа из запросов',
    'ask.check.running': 'проверяет…',
    'ask.check.ok': 'Числа проверены',
    'ask.check.unverified_one': '{{count}} число не проверено',
    'ask.check.unverified_few': '{{count}} числа не проверены',
    'ask.check.unverified_many': '{{count}} чисел не проверено',
    'ask.check.unverified_other': '{{count}} числа не проверены',
    'ask.kind.answer': 'Ответ',
    'ask.kind.ask_back': 'Уточните',
    'ask.kind.out_of_data': 'Нет в данных',
    'ask.basedOn_one': 'На основе {{count}} запроса',
    'ask.basedOn_few': 'На основе {{count}} запросов',
    'ask.basedOn_many': 'На основе {{count}} запросов',
    'ask.basedOn_other': 'На основе {{count}} запросов',
    'ask.showQueries': 'Показать запросы',
    'ask.hideQueries': 'Скрыть запросы',
    'ask.next': 'Спросить дальше',
    'ask.group.action': 'Что делать',
    'ask.group.reason': 'Почему',
    'ask.choose': 'Выберите',
    'ask.moreRows_one': 'и ещё {{count}} строка',
    'ask.moreRows_few': 'и ещё {{count}} строки',
    'ask.moreRows_many': 'и ещё {{count}} строк',
    'ask.moreRows_other': 'и ещё {{count}} строки',
    'ask.examples': 'Например',
    'ask.openFull': 'Открыть полностью',
    'ask.close': 'Закрыть',
    'ask.error.failed': 'Ответ не пришёл. Попробуйте ещё раз.',
    'ask.error.no_answer': 'Ответ не пришёл. Попробуйте ещё раз.',
    'ask.error.over_budget': 'Бюджет «Аналитика» на этот месяц исчерпан.',
    'ask.error.not_configured': '«Аналитик» сейчас недоступен.',
    'ask.error.forbidden_by_role': 'У вашей роли нет «Аналитика».',
    'ask.error.stopped': 'Вопрос остановился до ответа.',
    'ask.loadFailed': 'Не удалось загрузить вопросы.',
    'ask.retry': 'Попробовать снова',
    'ask.budget': 'Использовано {{share}} бюджета «Аналитика» на этот месяц.',
}

for (const [language, strings] of Object.entries({ en, et, de, ru })) {
    i18n.addResourceBundle(language, 'translation', strings, true, false)
}
